<template>
    <div>
        <div v-show="!vendor_companies" class="text-center mb-4">
            <p class="fw-700 f-110 mb-0">{{
                    $t(assessment_clients ? '본 평가를 안내한 원청사를 선택 후 귀사의 업체코드를 조회해 주세요.' : '귀사의 업체코드를 조회해주세요.')
                }}</p>
        </div>
        <div v-show="vendor_companies" class="text-center mt-3 mb-4">
            <p class="fw-700 f-110 mb-0">{{ $t('검색된 기업 리스트에서 평가신청할 기업을 선택해 주세요.') }}</p>
        </div>
        <b-card no-body bg-variant="light">
            <div class="p-3">
                <b-input-group>
                    <template v-if="assessment_clients" #prepend>
                        <b-dropdown right :text="clientLabel(form.selected_client)"
                                    menu-class="py-0 my-0">
                            <b-dropdown-item
                                v-for="(_client, _client_idx) in assessment_clients"
                                :key="`assessment-clients-${_client_idx}`"
                                :class="[{ 'bgray-200': form.selected_client && form.selected_client.value === _client.value }]"
                                @click="selectClient(_client)"
                            >
                                {{ clientLabel(_client) }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </template>
                    <b-form-input id="input-vendor_code"
                                  v-model="form.search_vendor_code"
                                  :placeholder="$t(`업체 코드 (3자 이상)`)"
                                  :disabled="loading"
                                  trim
                                  @keypress.enter="searchVendorCode"
                    />
                    <template #append>
                        <b-btn variant="primary"
                               :disabled="(form.search_vendor_code || '').trim().length < 3 || loading"
                               @click="searchVendorCode"
                        >
                            <div class="d-flex align-items-center">
                                <b-spinner v-if="loading" small class="mr-2"/>
                                {{ $t('업체코드 조회') }}
                            </div>
                        </b-btn>
                    </template>
                </b-input-group>
            </div>

            <b-table v-show="vendor_companies"
                     id="vendor-table"
                     ref="vendor-table"
                     :items="vendor_companies"
                     :fields="vendor_companies_fields"
                     :empty-text="$t('검색된 기업이 없습니다.')"
                     selectable
                     select-mode="single"
                     show-empty
                     hover
                     class="mb-0"
                     :per-page="pagination.perPage"
                     :current-page="pagination.currentPage"
                     table-variant="light"
                     @row-selected="selectCompany"
            >

                <template #head(selected)="{ label }">
                    <div class="d-flex align-items-center justify-content-between">
                        <div>{{ $t(label) }}</div>

                        <div>
                            {{ $t('업체 코드') }}
                            <div class="f-85">
                                ({{ $t('프로젝트 코드') }})
                            </div>
                        </div>
                    </div>
                </template>

                <template #cell(selected)="{ rowSelected, item }">
                    <b-row no-gutters class="mb-2">
                        <b-col cols="12">
                            <div class="border-top bg-light p-3">
                                <div
                                    class="d-flex justify-content-between align-items-center">
                                    <div class="d-flex align-items-start">
                                        <!-- 색만으로 구분하지 않도록 선택된 행에는 '선택됨' 배지를 함께 보인다 -->
                                        <template v-if="rowSelected">
                                                                            <span aria-hidden="true"
                                                                                  class="font-weight-bold text-primary f-140 mt-n2">&check;</span>
                                            <span class="sr-only">{{ $t('선택됨') }}</span>
                                        </template>
                                        <template v-else>
                                                                            <span aria-hidden="true"
                                                                                  class="gray-400 f-140 mt-n2">&check;</span>
                                            <span class="sr-only">{{ $t('선택 안 됨') }}</span>
                                        </template>
                                        <!-- <h5 class="f-110 mb-0 ml-2 mr-2">{{ item.company.company_name_dip }}</h5>
                                        <p class="f-95 mb-0 fw-400">({{ item.company.company_name_en_dip || '  -  ' }})</p> -->
                                        <div class="d-flex flex-column ml-2">
                                            <h5 class="f-110 mb-0">
                                                {{ item.company.company_name }}
                                                <b-badge v-if="rowSelected" variant="primary" class="ml-1 align-middle f-75" aria-hidden="true">{{ $t('선택됨') }}</b-badge>
                                            </h5>
                                            <p class="f-95 mb-0 fw-400">({{
                                                    item.company.company_name_en || '  -  '
                                                }})</p>
                                        </div>
                                    </div>
                                    <!-- <h5 class="pl-3 f-100 mb-0">{{ item.vendor_code }}</h5> -->
                                    <div>
                                        <h5 class="pl-3 f-100 mb-0">{{
                                                item.vendor_code
                                            }}</h5>
                                        <h6 class="pl-3 f-90 mb-0">({{
                                                item.project_code
                                            }})</h6>
                                    </div>
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                    <b-row no-gutters class="pb-0 mb-2 px-3">
                        <b-col cols="12" lg="4">
                            <p class="f-90 mb-1"> {{ $t('소재지') }}</p>
                            <div class="pr-lg-2">
                                <template
                                    v-if="!item.company.country && !item.company.country_en">
                                    <h5 class="f-100 mb-0">{{ ' - ' }}</h5>
                                </template>
                                <template
                                    v-else-if="!item.company.country && item.company.country_en">
                                    <h5 class="f-100 mb-0">{{
                                            item.company.country_en
                                        }}</h5>
                                </template>
                                <template v-else>
                                    <h5 class="f-100 mb-0">{{ item.company.country }}
                                        <template v-if="item.company.country_en">({{ item.company.country_en }})</template></h5>
                                </template>
                            </div>
                        </b-col>
                        <b-col v-if="assessment_clients" cols="12" lg="4">
                            <p class="f-90 mb-1"> {{ $t('원청사') }}</p>
                            <div class="">
                                <h5 class="f-100 mb-0">
                                    {{ item.hd_corporate || '-' }}
                                </h5>
                            </div>
                        </b-col>
                        <b-col cols="12" lg="4">
                            <p class="f-90 mb-1"> {{ $t('기업 담당자') }}</p>
                            <div class="pr-lg-2">
                                <template v-if="!item.manager">
                                    <h5>-</h5>
                                </template>
                                <template v-else>
                                    <h5 class="f-100 mb-0">
                                        {{item.manager.user_name }}
                                    </h5>
                                    <h5 class="f-95 mb-0">
                                        ({{ item.manager.e_mail }})
                                    </h5>
                                </template>
                            </div>
                        </b-col>
                    </b-row>
                    <b-row no-gutters class="mb-2 px-3">
                        <b-col cols="12" lg="4">
                            <p class="f-90 gray-600 fw-400 mb-1">{{ $t(`법인등록번호`) }}</p>
                            <div class="pr-lg-2">
                                <h6 class="gray-700 text-black fw-500 f-95 mb-0">
                                    {{ item.company.corporate_code || '-' }}</h6>
                            </div>
                        </b-col>
                        <b-col cols="12" lg="4">
                            <p class="f-90 gray-600 fw-400 mb-1">{{ $t(`사업자등록번호`) }}</p>
                            <div class="pr-lg-2">
                                <h6 class="gray-700 text-black fw-500 f-95 mb-0">
                                    {{ item.company.company_code || '-' }}</h6>
                            </div>
                        </b-col>
                        <b-col cols="12" lg="4">
                            <p class="f-90 gray-600 fw-400 mb-1">{{
                                    $t(`DUNS Number`)
                                }}</p>
                            <div class="">
                                <h6 class="gray-700 text-black fw-500 f-95 mb-0">
                                    {{ item.company.duns_number || '-' }}</h6>
                            </div>
                        </b-col>
                    </b-row>
                </template>
            </b-table>
        </b-card>

        <div v-show="is_searched" class="text-right f-95 py-2">
            <p class="fw-300 mb-0">{{ $t('해당기업이 없을 경우, 평가 주관기관 또는 아래 문의하기를 통해 문의해주시기 바랍니다.') }}</p>
        </div>

        <b-pagination v-show="is_searched"
                      v-model="pagination.currentPage"
                      class="my-3"
                      :total-rows="vendor_companies === null ? 0 : vendor_companies.length"
                      :per-page="pagination.perPage"
                      aria-controls="vendor-table"
                      align="center"
                      :disabled="loading"
        ></b-pagination>


        <div v-if="selected_vendor" class="mb-4">
            <!-- 대체(우선)평가 대상이면 일반 가이드 대신 안내 + 갈아타기 버튼 (priorityAssessment 테넌트) -->
            <PriorityAssessmentNotice v-if="priority_alt"
                                      :alt-vendor="priority_alt"
                                      :assessment-title="priority_assessment_title"
                                      @apply="applyPriorityAssessment"
            />
            <template v-else-if="!selected_vendor.validated">
                <un-validated-vendor-code-guide/>
            </template>
            <template v-else-if="selected_vendor.validated">
                <validated-vendor-code-guide/>
            </template>
        </div>

        <CommonRegisterFooter>
            <b-btn variant="blue"
                   :disabled="!selected_vendor || loading || !!priority_alt"
                   @click="handleNext"
            >
                {{ $t(`다음`) }}
            </b-btn>
        </CommonRegisterFooter>

    </div>
</template>

<script>
import dayjs from 'dayjs';
import {getDefaultPagination} from '@shared/utils/common';
import locale_computed from '@shared/mixin/locale_computed';
import CommonRegisterFooter from '@/components/auth/Register/_SubComponents/CommonRegisterFooter.vue';
import {mapActions, mapState} from 'vuex';
import ValidatedVendorCodeGuide
from '@/components/auth/Register/_SubComponents/RegisterStep1/ValidatedVendorCodeGuide.vue';
import UnValidatedVendorCodeGuide
from '@/components/auth/Register/_SubComponents/RegisterStep1/UnValidatedVendorCodeGuide.vue';
import PriorityAssessmentNotice
from '@/components/auth/Register/_SubComponents/RegisterStep1/PriorityAssessmentNotice.vue';
import {tenant, mergeTenantRegister} from '@shared/utils/tenant';

export default {
    name: 'RegisterStep1',
    components: {UnValidatedVendorCodeGuide, ValidatedVendorCodeGuide, CommonRegisterFooter, PriorityAssessmentNotice},
    mixins: [locale_computed],
    data() {
        return {
            loading: false,
            form: {
                search_vendor_code: null,
                selected_client: {
                    label: null,
                    value: null,
                },
            },

            is_searched: false,
            // syncVendorQuery 가 마지막으로 요청한 'ivvc|ivpc'
            last_vendor_query_key: null,
            pagination: getDefaultPagination(1, 3),
            vendor_companies: null,
            vendor_companies_fields: [
                {
                    key: 'selected',
                    label: '기업 목록',
                    thClass: '',
                    tdClass: 'align-middle p-0'
                }
            ],
        };
    },
    computed: {
        ...mapState('register', ['selected_vendor', 'remote_register_config']),
        // 서버(brands DB) 런타임 설정 우선, 코드 기본값 폴백
        tenant_register() {
            return mergeTenantRegister(tenant && tenant.register, this.remote_register_config);
        },
        // 테넌트 원청사 로스터 — 없으면 드롭다운 미표시 + project_group 'SAQ' 고정 (qesg 현행)
        assessment_clients() {
            return (this.tenant_register && this.tenant_register.clients) || null;
        },
        // priority_vendor_code 대체평가 안내 — priorityAssessment 테넌트에서만
        priority_alt() {
            if (!this.tenant_register || !this.tenant_register.priorityAssessment) return null;
            return (this.selected_vendor && this.selected_vendor.priority_vendor_code) || null;
        },
        priority_assessment_title() {
            const lines = tenant && tenant.login && tenant.login.titleLines;
            return lines ? lines.join(' ') : '참여중인 평가';
        },
    },
    created() {
        if (this.assessment_clients) {
            this.form.selected_client = this.assessment_clients[0];
        }
    },
    async mounted() {
        await this.initData();
    },
    methods: {
        ...mapActions('register', ['setSelectedVendor', 'setRegisterStep']),
        async initData() {
            this.form.search_vendor_code = null;
            this.pagination = getDefaultPagination(1, 3);
            this.vendor_companies = null;
            this.is_searched = false;

            const {ivpc, ivvc} = this.$route.query;

            if (this.assessment_clients) {
                this.form.selected_client = this.assessment_clients[0];

                // 초대 링크의 원청사 프리셀렉트 (custom steel/hyundai-v2 이관)
                if (ivpc) {
                    const _selected_ivpc = this.assessment_clients.find(({value}) => value === ivpc);
                    if (!_selected_ivpc) {
                        this.$showModal({
                            type: 'alert',
                            state: 'warning',
                            title: this.$t('잘못된 요청'),
                            message: this.$t('잘못된 주소입니다. "평가 주최사"를 직접 선택해주세요'),
                        });
                        this.restoreStep();
                        return;
                    }
                    this.form.selected_client = _selected_ivpc;
                }
            }

            if (ivvc && (!this.assessment_clients || this.form.selected_client.value)) {
                await this.searchAndSelect(ivvc);
            }
            this.restoreStep();
        },
        // ?step=2 로 들어왔으면 업체 재검증이 성공했을 때만 Step 2, 실패하면 step 을 지운다
        async restoreStep() {
            if (this.$route.query.step !== '2') return;
            await this.$nextTick();
            if (this.selected_vendor && !this.priority_alt) {
                this.setRegisterStep(2);
            } else {
                const {step, ...query} = this.$route.query;
                this.$router.replace({query});
            }
        },
        // 업체코드로 검색해 '정확히 같은 코드' 1건이면 자동 선택한다. 검색은 부분일치라
        // 첫 행이 다른 업체일 수 있다(ivvc=A12 인데 A123 이 먼저 오는 경우).
        async searchAndSelect(vendor_code) {
            this.form.search_vendor_code = vendor_code;
            if (!(await this.searchVendorCode())) return false;
            const code = vendor_code.trim().toLowerCase();
            const exact = this.vendor_companies.filter(v => v.vendor_code.toLowerCase() === code);
            if (exact.length !== 1) return false;
            this.vendor_companies = exact;
            this.pagination.currentPage = 1;
            await this.$nextTick();
            this.$refs['vendor-table'].selectRow(0);
            return true;
        },
        clientLabel(client) {
            const label = client && client.label;
            if (!label) return '';
            if (typeof label === 'string') return this.$t(label);
            return label[this.$i18n.locale] || label.ko;
        },
        warn(message, state = 'warning') {
            this.$showModal({type: 'alert', state, message: this.$t(message)});
        },
        // 원청사를 바꾸면 이전 원청사로 찾은 업체·선택을 비운다(주소의 ivpc 와 화면이 섞이지 않게)
        selectClient(client) {
            if (client === this.form.selected_client) return;
            this.form.selected_client = client;
            this.vendor_companies = null;
            this.is_searched = false;
            this.setSelectedVendor(null);
            this.syncVendorQuery(null);
        },
        // 고른 업체를 주소(ivvc, 원청사 있으면 ivpc)에 남긴다 — 새로고침·공유 시 initData 가 같은 업체를 다시 고른다. 검색어는 올리지 않는다.
        // 직전에 요청한 것과 같은 목적지면 다시 부르지 않는다: 원청사 변경 직후 테이블 선택 해제가 같은 replace 를 또 부르면
        // 전역 가드(100ms 안 같은 목적지 차단)가 두 번째를 막으면서 진행 중인 첫 번째까지 취소돼 주소가 옛 값으로 남는다.
        // 목적지가 다르면 새 replace 가 진행 중인 것을 취소하고 마지막 상태가 남는다.
        syncVendorQuery(vendor_code) {
            const query = {...this.$route.query};
            if (vendor_code) query.ivvc = vendor_code;
            else delete query.ivvc;
            if (this.assessment_clients && this.form.selected_client.value) query.ivpc = this.form.selected_client.value;
            else delete query.ivpc;
            const key = (query.ivvc || '') + '|' + (query.ivpc || '');
            if (key === this.last_vendor_query_key) return;
            this.last_vendor_query_key = key;
            this.$router.replace({query}).catch(() => {});
        },
        async searchVendorCode() {
            if (this.assessment_clients && !this.form.selected_client.value) {
                this.warn('"ESG 평가 주최 원청사"를 선택해주세요.');
                return false;
            }

            const vendor_code = (this.form.search_vendor_code || '').trim();
            if (vendor_code.length < 3) {
                this.warn('유효하지 않은 업체 코드입니다. \n 3자 이상의 업체 코드를 정확히 입력하여 주십시오.');
                return false;
            }

            this.loading = true;
            try {
                const {data} = await this.$axios.get('/register/vendor', {
                    params: {
                        vendor_code,
                        project_group: this.form.selected_client.value || 'SAQ',
                        search_mode: 0,
                        lang: this.$i18n.locale
                    }
                });
                this.vendor_companies = data.sort((a, b) => b.id - a.id);
                this.is_searched = true;
                return true;
            } catch (e) {
                this.warn('업체 검색에 실패했습니다. 잠시 후 다시 시도해주세요.', 'error');
                return false;
            } finally {
                this.loading = false;
            }
        },
        selectCompany(selected_company) {
            if (selected_company.length === 0) {
                this.setSelectedVendor(null);
                this.syncVendorQuery(null);
                return;
            }

            const selected_vendor = {...selected_company[0]};
            if (selected_vendor.validated_at) {
                let _validated_at = new Date(selected_vendor.validated_at);
                _validated_at = this.$i18n.locale === 'ko'
                    ? dayjs(_validated_at).locale('ko').format('YYYY년 M월 D일 HH:mm:ss')
                    : dayjs(_validated_at).locale('en').format('DD/MM/YYYY ddd HH:mm:ss');

                selected_vendor.validated_at = _validated_at;
            }

            this.setSelectedVendor(selected_vendor);
            this.syncVendorQuery(selected_vendor.vendor_code);
        },

        // 대체평가 갈아타기 — 우선 평가의 vendor_code 로 재검색 후 자동 선택
        async applyPriorityAssessment() {
            const alt = this.priority_alt;
            if (!alt || !alt.vendor_code) return;

            if (this.assessment_clients) {
                // 우선 평가의 주최사 역추적 — 매핑 불가 시 placeholder 아닌 첫 실제 주최사로 폴백
                const real_clients = this.assessment_clients.filter(c => c.value);
                this.form.selected_client = real_clients[0] || this.assessment_clients[0];
            }
            if (!(await this.searchAndSelect(alt.vendor_code))) {
                this.warn('업체 코드 데이터가 없습니다. 확인 후 재시도해주시기 바랍니다.');
            }
        },
        handleNext() {
            if (this.priority_alt) return; // 대체평가 대상은 안내 버튼으로만 진행
            if (this.isFieldEmpty(this.selected_vendor.vendor_code) || this.selected_vendor.vendor_code.length < 3) {
                this.warn('유효하지 않은 업체 코드입니다. \n 3자 이상의 업체 코드를 정확히 입력하여 주십시오.');
                return;
            }

            const h = this.$createElement;
            const messageVNode = h('div', {class: 'container-fluid'}, [
                ...(this.assessment_clients ? [h('div', {class: 'mb-3'}, [
                    h('div', {class: 'text-muted small'}, this.$t('원청사')),
                    h('div', {class: 'font-weight-bold h5 mb-2'}, this.clientLabel(this.form.selected_client))
                ])] : []),
                h('div', {class: 'mb-3'}, [
                    h('div', {class: 'text-muted small'}, this.$t('기업명')),
                    // Step 2 와 같은 규칙: 한국어는 국문명, 그 외는 영문명(없으면 국문명)
                    h('div', {class: 'font-weight-bold h5 mb-2'}, this.$i18n.locale === 'ko'
                        ? this.selected_vendor?.company.company_name_dip
                        : (this.selected_vendor?.company.company_name_en_dip || this.selected_vendor?.company.company_name_dip))
                ]),
                h('div', {class: 'mb-3'}, [
                    h('div', {class: 'text-muted small'}, this.$t('업체코드')),
                    h('div', {class: 'font-weight-bold h5 mb-2 text-primary'}, this.selected_vendor?.vendor_code)
                ]),
                h('div', {class: 'alert alert-info mt-4 mb-4 text-center'}, this.$t('해당 내용으로 평가신청을 계속 하시겠습니까?'))
            ]);


            this.$bvModal.msgBoxConfirm(messageVNode, {
                title: this.$t('평가신청 내용 확인'),
                okVariant: 'primary',
                okTitle: this.$t('확인'),
                cancelTitle: this.$t('취소'),
                hideHeaderClose: false,
                centered: true,
                size: 'md', // 필요시 'lg', 'sm' 등 조정
                headerBgVariant: 'dark',
                headerTextVariant: 'light',
                bodyClass: 'pb-0'
            })
                .then((value) => {
                    if (value)
                    {
                        // 새로고침/공유 시 ivvc(+ivpc)로 업체를 다시 찾아 Step 2 를 복원한다 (initData → restoreStep)
                        const query = {...this.$route.query, step: '2', ivvc: this.selected_vendor.vendor_code};
                        if (this.form.selected_client.value) query.ivpc = this.form.selected_client.value;
                        this.setRegisterStep(2);
                        this.$router.push({query});
                    }
                });
        },
    },
};
</script>

<style lang="scss" scoped>

</style>
